import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LEAD_DETAILS } from './constants';

// HWM-22633: Validate the lead's existing form-submitted shipping address.
//
// Runs structural validation on the backend (zip regex, 2-letter state code,
// non-empty street/city, ZIP↔state cross-check) against the canonical
// address columns (`street_address` / `apartment` / `city` / `state` /
// `zip`). On failure, returns a `data` map of field → list of error codes
// that the caller can render inline. No new address fields are sent — the
// endpoint uses what's already on the lead. No persisted "validated" flag —
// the next render recomputes `address_validation_status` via the BE
// `compute_address_validation_status` helper.
export type ValidateAddressFieldErrors = Record<string, string[]>;

export interface ValidateAddressErrorResponse {
    errors: string[];
    data?: ValidateAddressFieldErrors;
}

interface UseValidateLeadAddressArgs {
    onSuccess?: () => void;
    onValidationError?: (fields: ValidateAddressFieldErrors) => void;
}

const useValidateLeadAddress = (args: UseValidateLeadAddressArgs = {}) => {
    const { onSuccess, onValidationError } = args;
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({ leadId }: { leadId: string }) =>
        req({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/leads/${leadId}/validate-address/`,
            method: 'POST',
            body: JSON.stringify({})
        });

    return useMutation({
        mutationFn: request,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [LEAD_DETAILS] });
            setNotification({ message: 'Address validated' });
            onSuccess && onSuccess();
        },
        onError: (error: ValidateAddressErrorResponse) => {
            // Surface field-level codes to the caller so the section can
            // render inline messages. The legacy `errors` array stays the
            // top-level signal (`invalid_address`).
            if (error?.data && onValidationError) {
                onValidationError(error.data);
            }
            return error;
        }
    });
};

export default useValidateLeadAddress;
